import { useEffect, useMemo } from 'react';
import { Box, Grid, Typography, keyframes } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { createTitle } from '../utils/utils';
import { colorPrimary, colorTextBlack, colorTextGrey } from '../config/theme';
import { TEACHER_ROLE_TYPE } from '../utils/constant';
import _ from 'lodash';
import { setActiveModule } from '../redux/reducers/layoutSlice';
import {
  resetGoogleLogin,
  teacherDashboard
} from '../redux/reducers/authSlice';
import { getDashboardMetrics } from '../redux/reducers/analyticsSlice';
import {
  IMG_USER_ANALYTICS_ICON,
  IMG_ROLE_ANALYTICS_ICON,
  IMG_SCHOOL_ANALYTICS_ICON,
  IMG_VIEW_ANALYTICS_ICON,
  IMG_USER_GROUP_ANALYTICS_ICON
} from '../utils/imageUrls';
import DashboardMetricCard from './dashboard/DashboardMetricCard';
import DashboardMetricSkeleton from './dashboard/DashboardMetricSkeleton';
import RightSection from '../containers/teacherDashboard/rightSection';
import TeacherDetails from '../containers/teacherDashboard/leftSection/teacherDetails';
import Courses from '../containers/teacherDashboard/leftSection/courses';

const heroFade = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const floatOrb = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(16px, -12px) scale(1.08); }
`;

const DASHBOARD_WIDGETS = [
  {
    id: 'DASH-01',
    key: 'teacherCount',
    description: 'Teacher Count',
    icon: IMG_USER_ANALYTICS_ICON,
    accent: '#3B82F6'
  },
  {
    id: 'DASH-02',
    key: 'facilitatorCount',
    description: 'Facilitator Count',
    icon: IMG_ROLE_ANALYTICS_ICON,
    accent: '#8B5CF6'
  },
  {
    id: 'DASH-03',
    key: 'schoolCount',
    description: 'School Count',
    icon: IMG_SCHOOL_ANALYTICS_ICON,
    accent: '#10B981'
  },
  {
    id: 'DASH-04',
    key: 'publishedContentCount',
    description: 'Content Metrics',
    icon: IMG_VIEW_ANALYTICS_ICON,
    accent: '#F59E0B'
  },
  {
    id: 'DASH-05',
    key: 'resourceCount',
    description: 'Resource Metrics',
    icon: IMG_USER_GROUP_ANALYTICS_ICON,
    accent: '#EC4899'
  },
  {
    id: 'DASH-06',
    key: 'totalUsers',
    description: 'Total Users',
    icon: IMG_USER_ANALYTICS_ICON,
    accent: '#F4C24A'
  }
];

function AdminDashboard({
  displayName,
  dashboardMetrics,
  isDashboardMetricsLoading
}) {
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 94px)',
        height: 'calc(100vh - 94px)',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
        borderRadius: { xs: 0, md: '8px' },
        background:
          'linear-gradient(145deg, #e8eef5 0%, #f5f7fa 35%, #fef9ec 70%, #f0f4f8 100%)'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          right: -80,
          width: 420,
          height: 420,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${colorPrimary}55 0%, transparent 68%)`,
          animation: `${floatOrb} 9s ease-in-out infinite`,
          pointerEvents: 'none',
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -80,
          left: -100,
          width: 360,
          height: 360,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(139, 92, 246, 0.35) 0%, transparent 68%)',
          animation: `${floatOrb} 11s ease-in-out infinite reverse`,
          pointerEvents: 'none',
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '40%',
          left: '35%',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, transparent 70%)',
          pointerEvents: 'none',
          zIndex: 0
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          flexShrink: 0,
          width: '100%',
          boxSizing: 'border-box',
          borderRadius: { xs: '20px', md: '24px' },
          px: { xs: 2.5, md: 3 },
          py: { xs: 2, md: 2.5 },
          mb: { xs: 2, md: 2.5 },
          background: 'rgba(255, 255, 255, 0.45)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.65)',
          boxShadow:
            '0 8px 32px rgba(31, 41, 51, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
          animation: `${heroFade} 0.55s ease-out both`,
          overflow: 'hidden'
        }}
      >
        <Typography
          sx={{
            position: 'relative',
            fontSize: { xs: '1.65rem', sm: '2rem', md: '2.35rem' },
            fontWeight: 800,
            letterSpacing: '-0.03em',
            color: colorTextBlack,
            lineHeight: 1.15,
            mb: 0.5
          }}
        >
          Welcome back, {displayName}!
        </Typography>
        <Typography
          sx={{
            position: 'relative',
            color: colorTextGrey,
            fontSize: { xs: '0.875rem', md: '0.95rem' },
            fontWeight: 500
          }}
        >
          Central analytics and operational monitoring
        </Typography>
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          width: '100%',
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))'
          },
          gap: { xs: 2, md: 2.5, lg: 2.5 },
          alignContent: 'stretch',
          alignItems: 'stretch'
        }}
      >
        {isDashboardMetricsLoading &&
          DASHBOARD_WIDGETS.map((widget, index) => (
            <DashboardMetricSkeleton
              key={widget.id}
              index={index}
              accent={widget.accent}
            />
          ))}

        {!isDashboardMetricsLoading &&
          DASHBOARD_WIDGETS.map((widget, index) => (
            <DashboardMetricCard
              key={widget.id}
              icon={widget.icon}
              count={dashboardMetrics[widget.key] ?? 0}
              description={widget.description}
              accent={widget.accent}
              index={index}
            />
          ))}
      </Box>
    </Box>
  );
}

function TeacherDashboard() {
  return (
    <Grid container spacing={2}>
      <Grid item sm={12} md={6} lg={6}>
        <TeacherDetails />
        <Courses />
      </Grid>
      <Grid item sm={12} md={6} lg={6}>
        <RightSection />
      </Grid>
    </Grid>
  );
}

function Dashboard() {
  const dispatch = useDispatch();
  const { authData } = useSelector(state => state.auth);
  const { dashboardMetrics, isDashboardMetricsLoading } = useSelector(
    state => state.analytics
  );

  const isTeacher = _.includes(TEACHER_ROLE_TYPE, authData?.roleType);

  const displayName = useMemo(
    () => authData?.name?.split(' ')[0] || 'User',
    [authData?.name]
  );

  useEffect(() => {
    document.title = createTitle('Dashboard');
    dispatch(setActiveModule('dashboard'));
    dispatch(resetGoogleLogin());

    if (isTeacher) {
      dispatch(teacherDashboard());
    } else {
      dispatch(getDashboardMetrics());
    }
  }, [dispatch, isTeacher]);

  if (isTeacher) {
    return <TeacherDashboard />;
  }

  return (
    <AdminDashboard
      displayName={displayName}
      dashboardMetrics={dashboardMetrics}
      isDashboardMetricsLoading={isDashboardMetricsLoading}
    />
  );
}

export default Dashboard;
